<template>
    <div v-if="visible" class="shield-loader-container">
      <div class="shield-loader" :style="loaderSizeStyles">
        <!-- Orbiting particles -->
        <div class="orbit-container">
          <div 
            v-for="n in particleCount" 
            :key="n"
            class="orbit-particle"
            :style="getParticleStyles(n)"
          ></div>
        </div>
        
        <!-- Shield SVG -->
        <svg class="shield-wrapper" viewBox="-10 -10 120 140" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="shieldGradB" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" :style="`stop-color:${hexToRgba(secondaryColor, 0.2)}`"/>
              <stop offset="100%" :style="`stop-color:${hexToRgba(primaryColorDark, 0.3)}`"/>
            </linearGradient>
            <linearGradient id="innerGradB" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" :style="`stop-color:${hexToRgba(secondaryColor, 0.25)}`"/>
              <stop offset="100%" :style="`stop-color:${hexToRgba(secondaryColor, 0.05)}`"/>
            </linearGradient>
          </defs>
          
          <!-- Shadow/depth layer -->
          <path 
            :fill="hexToRgba(primaryColorDark, 0.4)" 
            d="M52 8 L92 18 L92 54 C92 86 52 114 52 114 C52 114 12 86 12 54 L12 18 Z"
          />
          
          <!-- Main shield -->
          <path 
            fill="url(#shieldGradB)" 
            :stroke="secondaryColor" 
            stroke-width="2.5" 
            d="M50 5 L90 15 L90 52 C90 84 50 112 50 112 C50 112 10 84 10 52 L10 15 Z"
          />
          
          <!-- Inner raised area -->
          <path 
            fill="url(#innerGradB)" 
            :stroke="hexToRgba(secondaryColor, 0.5)" 
            stroke-width="1" 
            d="M50 18 L78 25 L78 50 C78 72 50 92 50 92 C50 92 22 72 22 50 L22 25 Z"
          />
          
          <!-- Highlight edge -->
          <path 
            fill="none" 
            :stroke="hexToRgba(accentColor, 0.15)" 
            stroke-width="1" 
            d="M50 5 L90 15 L90 52" 
            stroke-linecap="round"
          />
          
          <!-- Checkmark -->
          <polyline 
            fill="none" 
            :stroke="accentColor" 
            stroke-width="4" 
            stroke-linecap="round" 
            stroke-linejoin="round"
            points="35,52 46,65 65,42"
          />
        </svg>
      </div>
      
      <!-- Loading text -->
      <span v-if="showText" class="loading-text" :style="{ color: accentColor }">
        {{ loadingText }}<span class="dots"></span>
      </span>
      <span v-if="showSubtext" class="subtext" :style="{ color: hexToRgba(accentColor, 0.5) }">
        {{ subtext }}
      </span>
    </div>
  </template>
  
  <script>
  export default {
    name: 'ShieldSpinner',
    
    props: {
      // Size
      size: {
        type: Number,
        default: 120
      },
      
      // Colors
      primaryColor: {
        type: String,
        default: '#0D7542'
      },
      primaryColorDark: {
        type: String,
        default: '#074024'
      },
      secondaryColor: {
        type: String,
        default: '#8DDD99'
      },
      accentColor: {
        type: String,
        default: '#FAF5E3'
      },
      
      // Text
      loadingText: {
        type: String,
        default: 'Securing'
      },
      subtext: {
        type: String,
        default: 'Encrypting your files'
      },
      showText: {
        type: Boolean,
        default: true
      },
      showSubtext: {
        type: Boolean,
        default: true
      },
      
      // Animation
      particleCount: {
        type: Number,
        default: 3
      },
      animationDuration: {
        type: Number,
        default: 2.4
      }
    },
    
    data() {
      return {
        visible: false,
        visibilityTimer: null
      }
    },
    
    mounted() {
      this.visibilityTimer = setTimeout(() => {
        this.visible = true
      }, 3000)
    },
    
    beforeDestroy() {
      if (this.visibilityTimer) {
        clearTimeout(this.visibilityTimer)
        this.visibilityTimer = null
      }
    },
    
    computed: {
      loaderSizeStyles() {
        return {
          width: `${this.size}px`,
          height: `${this.size * 1.2}px`
        }
      }
    },
    
    methods: {
      hexToRgba(hex, alpha) {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      },
      
      getParticleStyles(index) {
        const sizes = [12, 9, 6]
        const size = sizes[index - 1] || 6
        const delay = -((index - 1) * (this.animationDuration / this.particleCount))
        
        return {
          width: `${size}px`,
          height: `${size}px`,
          marginLeft: `-${size / 2}px`,
          marginTop: `-${size / 2}px`,
          background: this.secondaryColor,
          boxShadow: `0 0 10px ${this.hexToRgba(this.secondaryColor, 0.9)}, 0 0 20px ${this.hexToRgba(this.secondaryColor, 0.5)}`,
          animationDelay: `${delay}s`,
          animationDuration: `${this.animationDuration}s`
        }
      }
    }
  }
  </script>
  
  <style scoped>
  .shield-loader-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }
  
  .shield-loader {
    position: relative;
  }
  
  .shield-wrapper {
    position: relative;
    z-index: 5;
    width: 100%;
    height: 100%;
  }
  
  /* Orbit container */
  .orbit-container {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    pointer-events: none;
  }
  
  /* Orbiting particles */
  .orbit-particle {
    position: absolute;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    animation: orbit3d linear infinite;
  }
  
  @keyframes orbit3d {
    0% {
      transform: translateX(58px) translateY(0) scale(1);
      opacity: 1;
      z-index: 10;
    }
    20% {
      transform: translateX(35px) translateY(-12px) scale(0.7);
      opacity: 1;
      z-index: 10;
    }
    25% {
      transform: translateX(20px) translateY(-15px) scale(0.5);
      opacity: 0;
      z-index: -1;
    }
    45% {
      transform: translateX(-35px) translateY(-12px) scale(0.5);
      opacity: 0;
      z-index: -1;
    }
    50% {
      transform: translateX(-58px) translateY(0) scale(1);
      opacity: 1;
      z-index: 10;
    }
    75% {
      transform: translateX(0) translateY(18px) scale(1.15);
      opacity: 1;
      z-index: 10;
    }
    100% {
      transform: translateX(58px) translateY(0) scale(1);
      opacity: 1;
      z-index: 10;
    }
  }
  
  /* Loading text */
  .loading-text {
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 3px;
    text-transform: uppercase;
    font-family: 'Segoe UI', system-ui, sans-serif;
  }
  
  .dots::after {
    content: '';
    animation: dots 1.5s steps(4, end) infinite;
  }
  
  @keyframes dots {
    0%, 20% { content: ''; }
    40% { content: '.'; }
    60% { content: '..'; }
    80%, 100% { content: '...'; }
  }
  
  .subtext {
    font-size: 11px;
    letter-spacing: 1px;
    font-family: 'Segoe UI', system-ui, sans-serif;
  }
  </style>

